<template>
    <transition name="menu">
        <div v-if="isOpen" :class="{ 'mobile-menu': true, active: isSearchWindowActive }">
            <div class="mobile-menu__header">
                <Header :title="title" :isGoBack="!isMenuTab" @back="goBack" @close="toggleIsOpen(false)" />
            </div>

            <div class="mobile-menu__content">
                <ul v-if="isMenuTab" class="mobile-menu__content__menu">
                    <li class="mobile-menu__content__menu__item --search">
                        <form
                            class="mobile-menu__content__menu__item__search"
                            id="mobileSearchArea"
                            @submit.prevent="submitSearch"
                        >
                            <svg class="mobile-menu__content__menu__item__search__icon" @click="submitSearch">
                                <use xlink:href="@/themes/novaporta/assets/icons/sprite.svg#searchIcon"></use>
                            </svg>
                            <input
                                class="mobile-menu__content__menu__item__search__input"
                                type="text"
                                id="mobileSearch"
                                :placeholder="$t('searchBarInputPlaceholder', { count: activeProductCount })"
                                :value="searchValue"
                                @focus="toggleSearchWindowByValue(searchValue)"
                                @input="updateInput"
                            />
                            <SearchWindow
                                :isOpen="isSearchWindowActive"
                                :isLoading="isPreloaderSearchLoading"
                                :products="searchResultProducts"
                            />
                        </form>
                    </li>
                    <li class="mobile-menu__content__menu__item --catalog">
                        <div class="mobile-menu__content__menu__item__category" @click="setActiveTab('catalog')">
                            <span>
                                {{ $t('menuBarOptionsCatalog') }}
                            </span>

                            <span class="mobile-menu__content__menu__item__category__arrow">
                                <svg><use xlink:href="@/themes/novaporta/assets/icons/sprite.svg#arrowRightIcon"></use></svg>
                            </span>
                        </div>
                    </li>

                    <li
                        class="mobile-menu__content__menu__item"
                        v-for="(item, index) in globalSetting.header_pages"
                        v-if="item.is_active"
                        :key="index"
                    >
                        <a
                            :href="item.link"
                            class="mobile-menu__content__menu__item__link"
                            :style="{
                                display: 'flex',
                                gap: '5px',
                                alignItems: 'center',
                                flexDirection: item.position === 'left' ? 'row-reverse' : 'row',
                                width: 'max-content'
                            }"
                        >
                            <span
                                :style="{
                                    'font-size': '14px',
                                    color: setActiveClass(item.link) ? hoverColor(item) : 'var(--body-text)'
                                }"
                            >
                                {{ item.column_name }}
                            </span>
                            <img v-if="item.icon" width="16px" :style="{ 'object-fit': 'contain' }" :src="item.icon" />
                        </a>
                    </li>
                </ul>
                <ul v-else-if="isCategoryActive" class="mobile-menu__content__subcategory">
                    <li
                        class="mobile-menu__content__subcategory__item"
                        v-for="(item, index) in activeCategory.subcategories"
                        :key="index"
                    >
                        <div class="mobile-menu__content__subcategory__item__title">
                            <NuxtLink :to="localePath(`/${item.url}`)" class="mobile-menu__content__subcategory__link">
                                <span>{{ item.title }}</span>
                            </NuxtLink>
                        </div>
                        <ul class="mobile-menu__content__subcategory__items" v-if="item.subcategories.length">
                            <li
                                class="mobile-menu__content__subcategory__items__item"
                                v-for="(subcategory, idx) in item.subcategories"
                                :key="idx"
                            >
                                <NuxtLink
                                    :to="localePath(`/${subcategory.url}`)"
                                    class="mobile-menu__content__subcategory__items__item__link"
                                >
                                    <span>{{ subcategory.title }}</span>
                                </NuxtLink>
                            </li>
                        </ul>
                    </li>
                </ul>
                <ul v-else-if="isCatalogTab" class="mobile-menu__content__categories">
                    <li class="mobile-menu__content__categories__item" v-for="(item, index) in handledCategories" :key="index">
                        <button class="mobile-menu__content__categories__item__category" @click="triggerCategory(item)">
                            <div class="mobile-menu__content__categories__item__label">
                                <img v-if="item.image" :src="item.image" :alt="item.title" />
                                <img
                                    v-else
                                    src="@/themes/novaporta/assets/images/placeholders/product-square-image-not-found.png"
                                    :alt="item.title"
                                />
                                <span>{{ item.title }}</span>
                            </div>
                            <span v-if="item.subcategories.length" class="mobile-menu__content__categories__item__arrow">
                                <svg><use xlink:href="@/themes/novaporta/assets/icons/sprite.svg#arrowRightIcon"></use></svg>
                            </span>
                        </button>
                    </li>
                </ul>
            </div>

            <div class="mobile-menu__footer">
                <a class="mobile-menu__footer__phone__number" :href="'tel:' + mainPhoneNumber?.replace(/\s/g, '')">
                    <svg class="mobile-menu__footer__phone__icon">
                        <use xlink:href="@/themes/novaporta/assets/icons/sprite.svg#telephoneIcon"></use>
                    </svg>
                    <span>
                        {{ mainPhoneNumber }}
                    </span>
                </a>
            </div>
        </div>
    </transition>
</template>

<script>
import { mapGetters } from 'vuex';
import { createProductAgent } from '@/themes/novaporta/components/reusable/ProductAgent/types';
import VueTypes from 'vue-types';
import { createMenuCategoryItem, createMenuSubcategoryItem } from './types';
import HeaderNavigationMixin from '~/mixins/HeaderNavigationMixin';

export default {
    name: 'MobileMenu',
    mixins: [HeaderNavigationMixin],
    data() {
        return {
            selectedCategory: 'catalog',
            mainCategories: [],
            searchValue: '',
            isSearchWindowActive: false,
            isPreloaderSearchLoading: false,
            searchResultProducts: [],

            activeCategory: null
        };
    },
    computed: {
        ...mapGetters({
            categories: 'categories/getCategories',
            isOpen: 'mobile-menu/getIsOpen',
            activeTab: 'mobile-menu/getActiveTab',
            activeProductCount: 'global/getActiveProductCount',
            globalSetting: 'global/globalSettings',
            getCurrentTheme: 'global/getCurrentTheme',
            mainPhoneNumber: 'global/mainPhoneNumber'
        }),

        isMenuTab() {
            return this.activeTab === 'menu' ? true : false;
        },
        isCatalogTab() {
            return this.activeTab === 'catalog' ? true : false;
        },
        isCategoryActive() {
            return this.isCatalogTab && this.activeCategory ? true : false;
        },

        title() {
            if (this.isCategoryActive) return this.activeCategory.title;
            else if (this.isCatalogTab) return this.$t('globalTextAttributeCatalog');
            else return this.$t('mobileMenuTitle');
        },

        handledCategories() {
            return this.categories.map(category => {
                const subcategories = category.subcategories || []; // Check if subcategories exist, if not, assign an empty array

                return createMenuCategoryItem({
                    id: category.id,
                    title: category.categoryTitle,
                    image: category.categoryIcon,
                    url: category.categoryUrl,
                    subcategories: subcategories.map(subcategory =>
                        createMenuSubcategoryItem({
                            id: subcategory.id,
                            title: subcategory.subCategoryTitle,
                            url: subcategory.subCategoryUrl,
                            subcategories: subcategory.subcategories
                                ? subcategory.subcategories.map(subSubcategory =>
                                      createMenuSubcategoryItem({
                                          id: subSubcategory.id,
                                          title: subSubcategory.subCategoryTitle,
                                          url: subSubcategory.subCategoryUrl,
                                          subcategories: [] // ends here
                                      })
                                  )
                                : []
                        })
                    )
                });
            });
        }
    },
    components: {
        MyLinkBtn: () => import('@/themes/novaporta/components/ui/MyLinkBtn'),
        Header: () => import('./components/Header'),
        SearchWindow: () => import('./components/SearchWindow')
    },
    watch: {
        $route() {
            this.toggleIsOpen(false);
        },
        isOpen(newVal) {
            document.body.classList[newVal ? 'add' : 'remove']('--blocked');

            this.searchValue = '';
        },
        searchValue(newVal, oldVal) {
            const currentValue = newVal;

            if (newVal.trim() && oldVal.trim()) this.isPreloaderSearchLoading = true;

            this.toggleSearchWindowByValue(newVal),
                setTimeout(() => {
                    if (currentValue === this.searchValue) this.fetchProductsOnSearch(newVal);
                }, 500);
        }
    },
    methods: {
        async fetchProductsOnSearch(query) {
            try {
                this.isPreloaderSearchLoading = true;
                const { data } = await this.$api.search.getSearchResults(query);

                const newProducts = data.products.map(p => {
                    return createProductAgent({
                        active: p.active,
                        available: p.available,
                        id: p.productId,
                        name: p.title,
                        price: Number(p.price),
                        slug: p.url,
                        img: p.img,
                        oldPrice: p.old_price,
                        badges: p.badges_products, // TO DO: Require field badges
                        slides: [], // TO DO: Require slides field in product model
                        isOnSale: p.old_price ? true : false
                    });
                });

                this.searchResultProducts = newProducts;
            } catch (error) {
                console.error('Unvalid answer of search by query end-point', error);
            } finally {
                this.isPreloaderSearchLoading = false;
            }
        },
        updateInput(event) {
            this.searchValue = event.target.value;
        },
        toggleSearchWindowByValue(value) {
            if (value.trim()) this.isSearchWindowActive = true;
            else this.isSearchWindowActive = false;
        },
        toggleIsOpen(flag) {
            this.$store.dispatch('mobile-menu/setIsOpen', flag);
            if (flag === false) this.setActiveTab('menu');
        },
        goBack() {
            if (this.activeCategory) {
                this.setActiveCategory(null);
            } else {
                this.setActiveTab('menu');
            }
        },

        triggerCategory(item) {
            if (item.subcategories.length) this.setActiveCategory(item);
            else this.navigateTo(item.url);
        },
        setActiveCategory(category) {
            this.activeCategory = category;
        },
        setActiveTab(name) {
            this.$store.dispatch('mobile-menu/setActiveTab', name);
        },
        navigateTo(route) {
            this.$router.push({ path: this.localePath(`/${route}`) });
        },
        submitSearch() {}
    }
};
</script>

<style lang="scss">
@import 'styles';
</style>
